import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import SubScript from '@tiptap/extension-subscript';
import { TableKit } from '@tiptap/extension-table';
import { Box, LoadingOverlay, Paper, Stack, Text } from '@mantine/core';
import { DivExtension } from './extensions/DivExtension';
import { ReportEditorToolbar } from './ReportEditorToolbar';
import {
  ReportTemplate,
  TiptapJSON,
  ReportBindingData,
} from './types/template.types';
import { buildTemplateDocument } from './templates/neurocutaneous-template';
import { refreshPrefillableSections } from './utils/section-validation';
import styles from './ReportEditor.module.css';

export interface ReportEditorProps {
  /** Template to render */
  template: ReportTemplate;
  /** Initial content (if editing existing report) */
  initialContent?: TiptapJSON;
  /** Data for auto-populating fields */
  bindingData?: ReportBindingData;
  /** Called when content changes */
  onContentChange?: (content: TiptapJSON) => void;
  /** Whether editor is in read-only mode */
  readOnly?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Called when PDF preview is requested */
  onPreviewPDF?: () => void;
  /** Called when PDF export is requested */
  onExportPDF?: () => void;
  /** Called when print is requested */
  onPrint?: () => void;
  /** Whether PDF operations are loading */
  pdfLoading?: boolean;
}

/**
 * Ref handle for accessing editor internals
 */
export interface ReportEditorRef {
  /** Get the editor container element */
  getEditorElement: () => HTMLElement | null;
  /** Get the current content as JSON */
  getContent: () => TiptapJSON | null;
  /** Get the Tiptap editor instance */
  getEditor: () => Editor | null;
}

/**
 * Replace placeholder tokens in content with actual data
 * @param content - Tiptap JSON content with placeholders
 * @param data - Binding data to populate placeholders with
 * @returns Tiptap JSON content with placeholders replaced
 */
function populateBindingData(
  content: TiptapJSON,
  data: ReportBindingData
): TiptapJSON {
  const jsonString = JSON.stringify(content);

  // Replace patient placeholders
  let populated = jsonString
    .replace(/\{\{patient\.name\}\}/g, data.patient.name || '-')
    .replace(/\{\{patient\.birthDate\}\}/g, data.patient.birthDate || '-')
    .replace(/\{\{patient\.gender\}\}/g, translateGender(data.patient.gender))
    .replace(/\{\{patient\.identifier\}\}/g, data.patient.identifier || '-');

  // Replace order placeholders
  populated = populated
    .replace(
      /\{\{order\.identifier\}\}/g,
      data.order.identifier || data.order.id
    )
    .replace(/\{\{order\.orderDate\}\}/g, formatDate(data.order.orderDate))
    .replace(/\{\{order\.requesterName\}\}/g, data.order.requesterName || '-')
    .replace(/\{\{order\.testName\}\}/g, data.order.testName || '-')
    .replace(/\{\{order\.reasonCode\}\}/g, data.order.reasonCode || '-');

  // Replace specimen placeholders
  if (data.specimen) {
    populated = populated
      .replace(/\{\{specimen\.type\}\}/g, data.specimen.type || '-')
      .replace(
        /\{\{specimen\.collectionDate\}\}/g,
        formatDate(data.specimen.collectionDate)
      )
      .replace(
        /\{\{specimen\.receivedDate\}\}/g,
        formatDate(data.specimen.receivedDate)
      );
  } else {
    populated = populated
      .replace(/\{\{specimen\.type\}\}/g, '-')
      .replace(/\{\{specimen\.collectionDate\}\}/g, '-')
      .replace(/\{\{specimen\.receivedDate\}\}/g, '-');
  }

  // Replace report placeholders
  populated = populated.replace(
    /\{\{report\.reportDate\}\}/g,
    formatDate(data.report.reportDate)
  );

  // Replace signature placeholders with empty for now
  populated = populated
    .replace(/\{\{signature\.labDirector\.name\}\}/g, '_________________')
    .replace(/\{\{signature\.labDirector\.title\}\}/g, ' ')
    .replace(/\{\{signature\.labDirector\.date\}\}/g, '_________________')
    .replace(/\{\{signature\.responsibleDoctor\.name\}\}/g, '_________________')
    .replace(/\{\{signature\.responsibleDoctor\.title\}\}/g, ' ')
    .replace(
      /\{\{signature\.responsibleDoctor\.date\}\}/g,
      '_________________'
    );

  const parsed = JSON.parse(populated);
  return cleanEmptyTextNodes(parsed);
}

/**
 * Recursively clean empty text nodes from Tiptap JSON
 * ProseMirror/Tiptap does not allow empty text nodes
 * @param node - Tiptap JSON node to clean
 * @returns Cleaned Tiptap JSON node
 */
function cleanEmptyTextNodes(node: TiptapJSON): TiptapJSON {
  if (!node.content) {
    return node;
  }

  // Filter out empty text nodes and recursively clean children
  const cleanedContent = node.content
    .filter((child: TiptapJSON) => {
      // Remove text nodes with empty string
      if (child.type === 'text' && child.text === '') {
        return false;
      }
      return true;
    })
    .map((child: TiptapJSON) => cleanEmptyTextNodes(child));

  return {
    ...node,
    content: cleanedContent.length > 0 ? cleanedContent : undefined,
  };
}

/**
 * Translate gender code using i18n (used outside React in data binding)
 * @param gender - Gender code (male, female, other)
 * @returns Translated gender string
 */
function translateGender(gender?: string): string {
  const key = gender?.toLowerCase();
  if (key === 'male' || key === 'female' || key === 'other') {
    return i18n.t(`reportEditor.gender.${key}`, { ns: 'lims' });
  }
  return '-';
}

/**
 * Format ISO date string to Greek format
 * @param dateString - ISO date string to format
 * @returns Formatted date string in Greek locale
 */
function formatDate(dateString?: string): string {
  if (!dateString) {
    return '-';
  }
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('el-GR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * Rich text report editor based on Tiptap
 * Supports non-editable headers, editable sections, and data binding
 */
export const ReportEditor = forwardRef<ReportEditorRef, ReportEditorProps>(
  (
    {
      template,
      initialContent,
      bindingData,
      onContentChange,
      readOnly = false,
      loading = false,
      onPreviewPDF,
      onExportPDF,
      onPrint,
      pdfLoading = false,
    },
    ref
  ) => {
    const { t } = useTranslation('lims');
    const [isInitialized, setIsInitialized] = useState(false);
    const hasSetInitialContent = useRef(false);
    const editorContainerRef = useRef<HTMLDivElement>(null);

    // Configure Tiptap extensions
    // Note: StarterKit already includes Link and Underline by default
    const extensions = useMemo(
      () => [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3, 4],
          },
          // Configure Link through StarterKit to avoid duplicates
          link: {
            openOnClick: false,
          },
        }),
        Superscript,
        SubScript,
        Highlight.configure({
          multicolor: true,
        }),
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        TableKit,
        DivExtension,
      ],
      []
    );

    // Build document content from template or initial content
    const documentContent = useMemo(() => {
      if (initialContent) {
        // Editing existing report: swap prefillable sections (e.g. patient-info)
        // with fresh template versions so binding data is always current
        const refreshed = refreshPrefillableSections(initialContent, template);
        return bindingData
          ? populateBindingData(refreshed, bindingData)
          : refreshed;
      }

      // Build from template
      const templateDoc = buildTemplateDocument(template);
      return bindingData
        ? populateBindingData(templateDoc, bindingData)
        : templateDoc;
    }, [template, initialContent, bindingData]);

    // Initialize editor
    const editor = useEditor({
      extensions,
      content: documentContent,
      editable: !readOnly,
      onUpdate: ({ editor }) => {
        if (onContentChange && isInitialized) {
          onContentChange(editor.getJSON());
        }
      },
      onCreate: ({ editor }) => {
        setIsInitialized(true);
        // Populate initial content for PDF export to work immediately
        if (onContentChange) {
          onContentChange(editor.getJSON());
        }
      },
    });

    // Set content only on first load — after that the editor owns the content
    useEffect(() => {
      if (!editor || !documentContent || hasSetInitialContent.current) {
        return;
      }
      editor.commands.setContent(documentContent);
      hasSetInitialContent.current = true;
    }, [editor, documentContent]);

    // Update editable state
    useEffect(() => {
      if (editor) {
        editor.setEditable(!readOnly);
      }
    }, [editor, readOnly]);

    // Get current JSON content
    const getContent = useCallback((): TiptapJSON | null => {
      return editor?.getJSON() || null;
    }, [editor]);

    // Expose getContent method through ref
    useEffect(() => {
      if (editor) {
        (
          editor as Editor & { getReportContent?: () => TiptapJSON | null }
        ).getReportContent = getContent;
      }
    }, [editor, getContent]);

    // Expose ref methods
    useImperativeHandle(
      ref,
      () => ({
        getEditorElement: () => editorContainerRef.current,
        getContent,
        getEditor: () => editor,
      }),
      [editor, getContent]
    );

    if (!editor) {
      return (
        <Paper p="md" withBorder pos="relative" mih={400}>
          <LoadingOverlay visible />
        </Paper>
      );
    }

    return (
      <Stack gap="md" className={styles.reportEditor}>
        <LoadingOverlay visible={loading} />

        <Paper
          ref={editorContainerRef}
          withBorder
          className={styles.editorContainer}
          data-readonly={readOnly}
        >
          <Stack gap={0}>
            {!readOnly && (
              <ReportEditorToolbar
                editor={editor}
                onPreviewPDF={onPreviewPDF}
                onExportPDF={onExportPDF}
                onPrint={onPrint}
                pdfLoading={pdfLoading}
              />
            )}
            <Box p="md">
              <EditorContent editor={editor} className={styles.editorContent} />
            </Box>
          </Stack>
        </Paper>

        {!readOnly && (
          <Text size="xs" c="dimmed">
            {t('reportEditor.readOnlyHint')}
          </Text>
        )}
      </Stack>
    );
  }
);

ReportEditor.displayName = 'ReportEditor';

export default ReportEditor;
